import { useState } from 'react';
import { Bot, X, Loader2, Hash, Shuffle, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import agentesService from '../services/agentesService';

// Modal de criar agente — Fase 2.
//
// Substitui o prompt() simples por um formulário com:
// - Nome obrigatório (até 120 chars).
// - Tipo (usar_rag): palavra-chave / híbrido / IA total. Default: híbrido.
// - Persona opcional (texto livre que sobrescreve a persona padrão do RAG).
//
// O modo híbrido (usar_rag = 'fallback') é o recomendado: keyword tenta
// casar primeiro, e só vai pro RAG se nada bater. Reduz custo e latência.
const TIPOS = [
  {
    id: 'desligado',
    titulo: 'Palavra-chave (sem IA)',
    descricao: 'Responde só com regras keyword cadastradas. Não usa OpenAI.',
    Icon: Hash,
    cor: 'text-ink-2',
  },
  {
    id: 'fallback',
    titulo: 'Híbrido (recomendado)',
    descricao: 'Keyword tenta primeiro; se nada casar, IA responde com RAG.',
    Icon: Shuffle,
    cor: 'text-emerald-400',
  },
  {
    id: 'sempre',
    titulo: 'IA total (RAG sempre)',
    descricao: 'Toda mensagem passa pelo RAG. Máxima naturalidade, maior custo.',
    Icon: Sparkles,
    cor: 'text-brand-400',
  },
];

export default function NewAgentModal({ sessao, onClose, onCreated }) {
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('fallback');
  const [persona, setPersona] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function handleCriar() {
    if (!nome.trim()) {
      toast.warn('Informe o nome do agente');
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        nome: nome.trim(),
        status: 'ativo',
        cooldown_segundos: 30,
        usar_rag: tipo,
        persona_prompt: persona.trim() ? persona.trim() : null,
        sessoes: [{ sessao_tipo: sessao.sessao_tipo, sessao_id: sessao.id }],
      };
      const r = await agentesService.criar(payload);
      onCreated?.(r.data);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Erro ao criar agente';
      const detalhes = e?.response?.data?.detalhes;
      toast.error(detalhes ? `${msg}: ${detalhes.join(', ')}` : msg);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface-1 border border-white/[0.06] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/[0.06]">
          <div className="w-8 h-8 rounded-lg bg-brand-500/10 border border-brand-500/40 grid place-items-center">
            <Bot className="w-4 h-4 text-brand-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-ink-1">Novo agente</h2>
            <div className="text-[11px] text-ink-4 truncate">
              Para sessão <span className="uppercase tracking-wider">{sessao.sessao_tipo}</span> — {sessao.nome}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-surface-2 text-ink-3 hover:text-ink-1"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Nome */}
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1.5">Nome do agente</label>
            <input
              type="text"
              autoFocus
              maxLength={120}
              placeholder="Ex.: Atendente do Curso X"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="bg-base border border-white/[0.06] rounded p-2.5 text-sm w-full text-white"
            />
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-2">Tipo</label>
            <div className="space-y-2">
              {TIPOS.map((t) => {
                const sel = tipo === t.id;
                const I = t.Icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTipo(t.id)}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                      sel
                        ? 'bg-brand-500/10 border-brand-500/40'
                        : 'bg-base border-white/[0.06] hover:bg-surface-1'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-md grid place-items-center flex-shrink-0 ${sel ? 'bg-brand-500/20' : 'bg-surface-1 border border-white/[0.06]'}`}>
                      <I className={`w-4 h-4 ${t.cor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold ${sel ? 'text-brand-200' : 'text-ink-2'}`}>
                        {t.titulo}
                      </div>
                      <div className="text-[11px] text-ink-4 mt-0.5">
                        {t.descricao}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Persona (somente quando IA está envolvida) */}
          {tipo !== 'desligado' && (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1.5">
                Persona <span className="text-ink-4 font-normal">(opcional)</span>
              </label>
              <textarea
                placeholder='Ex.: "Você é um atendente consultivo do Curso de Análise de Redes. Tom: simpático, direto, sem jargão técnico desnecessário."'
                value={persona}
                onChange={(e) => setPersona(e.target.value)}
                maxLength={2000}
                className="bg-base border border-white/[0.06] rounded p-2.5 text-sm w-full text-white min-h-[100px] resize-y"
              />
              <p className="text-[10px] text-ink-4 mt-1">
                Sobrescreve a persona padrão. Deixe em branco para usar "vendedor consultivo" genérico.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-base/50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded text-xs font-semibold text-ink-2 hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            onClick={handleCriar}
            disabled={salvando || !nome.trim()}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2 rounded text-xs font-semibold text-white"
          >
            {salvando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {salvando ? 'Criando...' : 'Criar agente'}
          </button>
        </div>
      </div>
    </div>
  );
}
